import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import DashboardShell from "@/components/layout/SmartShell";
import CreditCostStrip from "@/components/credits/CreditCostStrip";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Sparkles, PenTool, Wand2, Copy, RotateCcw, Check } from "lucide-react";

/**
 * Wordsmith — guided creative writing & idea engine.
 * ---------------------------------------------------------------
 * Built on the Ad-Generator pattern: structured brief → JSON → display.
 * One product, eight formats — ad copy, scripts, captions, product
 * descriptions, articles, creative writing, research Q&A, idea packs.
 */

const FORMATS = [
  { id: "ad_copy",      label: "Ad Copy",                  hint: "Headline + body + CTA + 3 variants"  },
  { id: "script",       label: "Short-form Script",        hint: "TikTok / Reel / YouTube script"      },
  { id: "caption",      label: "Social Caption",           hint: "Platform-tuned, hashtags included"   },
  { id: "product_desc", label: "Product Description",      hint: "Launch-ready e-commerce copy"        },
  { id: "article",      label: "Article / Blog Draft",     hint: "250-1500 word editorial draft"       },
  { id: "creative",     label: "Creative Writing",         hint: "Story, poem, brand narrative"        },
  { id: "research_qa",  label: "Research / Q&A",           hint: "Structured research-style answer"    },
  { id: "idea_pack",    label: "Idea Pack",                hint: "8-12 numbered concept variations"    },
];

const TONES = [
  { id: "confident",    label: "Confident" },
  { id: "luxury",       label: "Luxury" },
  { id: "warm",         label: "Warm / inviting" },
  { id: "bold",         label: "Bold / high-energy" },
  { id: "playful",      label: "Playful / witty" },
  { id: "premium",      label: "Premium / refined" },
  { id: "documentary",  label: "Documentary / earnest" },
  { id: "edgy",         label: "Edgy / urban" },
];

const LENGTHS = [
  { id: "short",  label: "Short · tight" },
  { id: "medium", label: "Medium · balanced" },
  { id: "long",   label: "Long · in-depth" },
];

const PLATFORMS = [
  { id: "general",   label: "General / brand voice" },
  { id: "instagram", label: "Instagram" },
  { id: "tiktok",    label: "TikTok" },
  { id: "youtube",   label: "YouTube" },
  { id: "email",     label: "Email" },
  { id: "web",       label: "Web / blog" },
  { id: "press",     label: "Press / editorial" },
];

export default function WordsmithPage() {
  const { user } = useAuth();
  const [brief, setBrief] = useState({
    format:   "ad_copy",
    topic:    "",
    audience: "",
    tone:     "confident",
    length:   "medium",
    platform: "general",
    keywords: "",
    notes:    "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  if (!user) return <Navigate to="/login" replace />;

  const onGenerate = async () => {
    setError(""); setResult(null);
    if (!brief.topic.trim()) { setError("Tell us what to write about — fill in the topic above."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/writing/draft", {
        ...brief,
        topic:    brief.topic.trim(),
        audience: brief.audience.trim() || "general",
        keywords: brief.keywords.trim(),
        notes:    brief.notes.trim(),
      });
      setResult(data.result);
    } catch (err) {
      setError(formatApiErrorDetail(err) || "Writing engine failed — please retry.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {}
  };

  const fmt = FORMATS.find((f) => f.id === brief.format);

  return (
    <DashboardShell>
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-10" data-testid="wordsmith-page">
        <div className="mb-7">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-3">
            <PenTool className="w-3.5 h-3.5" /> Wordsmith · Writing & Ideas
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#F4F4F0]">Wordsmith</h1>
          <p className="text-[#A0A0A0] text-sm mt-2 max-w-2xl">
            Eight formats. One studio. Brief us once — get production-ready copy plus three alternate angles, every time.
          </p>
        </div>

        {!result && (
          <>
            {/* Format picker */}
            <div className="mb-5">
              <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">Pick a format</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="wordsmith-format-grid">
                {FORMATS.map((f) => {
                  const active = brief.format === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setBrief((b) => ({ ...b, format: f.id }))}
                      data-testid={`wordsmith-format-${f.id}`}
                      className={`text-left px-3 py-2.5 border transition-colors ${
                        active
                          ? "border-[#D4AF37] bg-[#D4AF37]/5"
                          : "border-[#1F1F1F] hover:border-[#D4AF37]/50 bg-[#080808]"
                      }`}
                    >
                      <p className={`text-xs font-bold ${active ? "text-[#D4AF37]" : "text-[#F4F4F0]"}`}>{f.label}</p>
                      <p className="text-[10px] text-[#A0A0A0] mt-0.5 leading-snug line-clamp-2">{f.hint}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Brief */}
            <div className="space-y-5 border border-[#1F1F1F] bg-[#0A0A0A] p-5 sm:p-6">
              <Field label={`What to write — ${fmt?.label || "topic"}`} required>
                <textarea
                  data-testid="wordsmith-topic"
                  rows={3}
                  value={brief.topic}
                  onChange={(e) => setBrief((b) => ({ ...b, topic: e.target.value }))}
                  placeholder={
                    brief.format === "ad_copy"      ? "e.g. luxury watch brand launching its first titanium dive piece"
                    : brief.format === "script"     ? "e.g. 30-second TikTok script for a vegan skincare line"
                    : brief.format === "caption"    ? "e.g. carousel post announcing a new sneaker drop"
                    : brief.format === "product_desc" ? "e.g. cold-brew coffee subscription, 12oz pouch, single-origin Ethiopian"
                    : brief.format === "article"    ? "e.g. why first-time founders should skip Series A optimization"
                    : brief.format === "creative"   ? "e.g. short story about an artist who paints with sound"
                    : brief.format === "research_qa"? "e.g. what is the difference between H.264 and H.265 for ad delivery?"
                                                    : "e.g. ten hooks for a fitness brand's new ad campaign"
                  }
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0] resize-none"
                />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Target audience">
                  <input
                    data-testid="wordsmith-audience"
                    value={brief.audience}
                    onChange={(e) => setBrief((b) => ({ ...b, audience: e.target.value }))}
                    placeholder="e.g. urban millennials, indie founders, brides-to-be"
                    className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
                  />
                </Field>
                <Selector label="Tone"     value={brief.tone}     options={TONES}     onChange={(v) => setBrief((b) => ({ ...b, tone: v }))}     testId="wordsmith-tone" />
                <Selector label="Length"   value={brief.length}   options={LENGTHS}   onChange={(v) => setBrief((b) => ({ ...b, length: v }))}   testId="wordsmith-length" />
                <Selector label="Platform" value={brief.platform} options={PLATFORMS} onChange={(v) => setBrief((b) => ({ ...b, platform: v }))} testId="wordsmith-platform" />
              </div>

              <Field label="Keywords to weave in (optional)">
                <input
                  data-testid="wordsmith-keywords"
                  value={brief.keywords}
                  onChange={(e) => setBrief((b) => ({ ...b, keywords: e.target.value }))}
                  placeholder="e.g. titanium, dive watch, water-resistant"
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
                />
              </Field>

              <Field label="Notes, references, must-haves (optional)">
                <textarea
                  data-testid="wordsmith-notes"
                  rows={3}
                  value={brief.notes}
                  onChange={(e) => setBrief((b) => ({ ...b, notes: e.target.value }))}
                  placeholder="Anything specific — brand voice rules, taglines to preserve, things to avoid."
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0] resize-none"
                />
              </Field>
            </div>

            <CreditCostStrip feature="script_help" quality="pro" label={fmt?.label || "Draft"} />

            {error && <div className="my-4 px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid="wordsmith-error">{error}</div>}

            <button
              onClick={onGenerate}
              disabled={busy}
              data-testid="wordsmith-generate-button"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#F0C84B] text-[#080808] font-bold py-3.5 text-sm uppercase tracking-wider transition-colors disabled:opacity-50"
            >
              {busy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Writing…</>
                : <><Wand2 className="w-4 h-4" /> Generate {fmt?.label}</>}
            </button>
            <p className="text-[10px] text-[#5A5A5A] uppercase tracking-wider mt-3 text-center">Every draft debits credits from your wallet</p>
          </>
        )}

        {result && <WordsmithResult result={result} format={brief.format} onReset={() => { setResult(null); setError(""); }} onCopy={copy} copiedKey={copiedKey} />}
      </div>
    </DashboardShell>
  );
}

function WordsmithResult({ result, format, onReset, onCopy, copiedKey }) {
  return (
    <div data-testid="wordsmith-result" className="space-y-5">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">{FORMATS.find((f) => f.id === format)?.label || "Draft ready"}</p>
          <h2 className="font-display text-2xl font-bold text-[#F4F4F0] mt-1">{result.title || "Untitled draft"}</h2>
          {result.tone_notes && <p className="text-[10px] text-[#A0A0A0] mt-1">Tone · {result.tone_notes}</p>}
        </div>
        <button onClick={onReset} data-testid="wordsmith-reset" className="text-[10px] uppercase tracking-wider text-[#A0A0A0] hover:text-[#D4AF37] border border-[#2A2A2A] hover:border-[#D4AF37] px-3 py-1.5">
          <RotateCcw className="w-3 h-3 inline mr-1" /> New draft
        </button>
      </header>

      {result.summary && (
        <Section title="Summary">{result.summary}</Section>
      )}

      <Section title="Primary draft" onCopy={() => onCopy("primary", result.primary)} copied={copiedKey === "primary"}>
        <pre className="font-mono text-sm text-[#F4F4F0] whitespace-pre-wrap leading-relaxed">{result.primary}</pre>
        {result.cta && <p className="text-xs text-[#D4AF37] mt-3 font-bold">CTA · {result.cta}</p>}
        {typeof result.word_count === "number" && (
          <p className="text-[10px] text-[#5A5A5A] uppercase tracking-wider mt-3">{result.word_count} words</p>
        )}
      </Section>

      {Array.isArray(result.variants) && result.variants.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] mb-3">Alternate angles · {result.variants.length}</p>
          <div className="space-y-3">
            {result.variants.map((v, i) => (
              <div key={i} data-testid={`wordsmith-variant-${i + 1}`} className="border border-[#1F1F1F] bg-[#0A0A0A] p-4 relative group">
                <span className="absolute top-2 right-3 text-[#D4AF37] text-xs font-bold">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-sm text-[#F4F4F0] leading-relaxed whitespace-pre-wrap pr-8">{v}</p>
                <button
                  onClick={() => onCopy(`var-${i}`, v)}
                  data-testid={`wordsmith-variant-copy-${i + 1}`}
                  className="absolute bottom-2 right-2 text-[10px] uppercase tracking-wider text-[#5A5A5A] hover:text-[#D4AF37] px-2 py-1 border border-[#2A2A2A]"
                >
                  {copiedKey === `var-${i}` ? <><Check className="w-3 h-3 inline mr-1" />Copied</> : <><Copy className="w-3 h-3 inline mr-1" />Copy</>}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(result.tags) && result.tags.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] mb-2">Tags</p>
          <div className="flex flex-wrap gap-2">
            {result.tags.map((t, i) => (
              <span key={i} className="text-[10px] uppercase tracking-wider text-[#A0A0A0] border border-[#2A2A2A] px-2 py-1">{t}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children, onCopy, copied }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0]">{title}</p>
        {onCopy && (
          <button onClick={onCopy} className="text-[10px] uppercase tracking-wider text-[#5A5A5A] hover:text-[#D4AF37] px-2 py-1 border border-[#2A2A2A]">
            {copied ? <><Check className="w-3 h-3 inline mr-1" />Copied</> : <><Copy className="w-3 h-3 inline mr-1" />Copy</>}
          </button>
        )}
      </div>
      <div className="border border-[#1F1F1F] bg-[#0A0A0A] p-5 text-sm text-[#F4F4F0] leading-relaxed">{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </span>
      {children}
    </label>
  );
}

function Selector({ label, value, options, onChange, testId }) {
  return (
    <Field label={label}>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
      >
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </Field>
  );
}
